if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
        this.checkboxInput.disabled = false;
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.hiddenControlField.disabled = true;
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.firstNameInput = this.querySelector(`#Recipient-first-name-${this.dataset.sectionId}`);
        this.lastNameInput = this.querySelector(`#Recipient-last-name-${this.dataset.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.currentProductVariantId = this.dataset.productVariantId;
        this.addEventListener('change', this.onChange.bind(this));
        this.onChange();
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        // Keep the canonical (hidden) "Recipient name" in sync with the first/last name fields.
        this.updateRecipientName = this.updateRecipientName.bind(this);
        this.firstNameInput.addEventListener('input', this.updateRecipientName);
        this.lastNameInput.addEventListener('input', this.updateRecipientName);

        // First/last name are required whenever the gift checkbox is checked (i.e. when the
        // recipient email is required). The product form is rendered with `novalidate` and
        // Shopify never validates the name server-side, so block the add-to-cart click here.
        this.handleSubmitClick = this.onSubmitClick.bind(this);
        this.submitButtons = Array.from(
          this.closest('product-form')?.querySelectorAll('.product-form__submit') || []
        );
        this.submitButtons.forEach((button) => button.addEventListener('click', this.handleSubmitClick, true));

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
          }
        });

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });
      }

      disconnectedCallback() {
        this.firstNameInput.removeEventListener('input', this.updateRecipientName);
        this.lastNameInput.removeEventListener('input', this.updateRecipientName);
        this.submitButtons?.forEach((button) => button.removeEventListener('click', this.handleSubmitClick, true));

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      onChange() {
        if (this.checkboxInput.checked) {
          this.enableInputFields();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormExpanded;
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormCollapsed;
        }
      }

      inputFields() {
        return [this.emailInput, this.firstNameInput, this.lastNameInput, this.messageInput, this.sendonInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.nameInput, this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
        if (this.nameInput) this.nameInput.value = '';
      }

      updateRecipientName() {
        if (!this.nameInput) return;
        const parts = [this.firstNameInput.value.trim(), this.lastNameInput.value.trim()].filter(Boolean);
        this.nameInput.value = parts.join(' ');
      }

      onSubmitClick(event) {
        if (!this.checkboxInput.checked) return;

        this.updateRecipientName();

        const missingFields = [];
        if (!this.firstNameInput.value.trim()) missingFields.push('first_name');
        if (!this.lastNameInput.value.trim()) missingFields.push('last_name');

        if (missingFields.length === 0) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.showRequiredErrors(missingFields);
      }

      showRequiredErrors(keys) {
        this.clearErrorMessage();
        this.errorMessageWrapper.hidden = false;
        this.errorMessage.innerText = this.defaultErrorHeader;

        const fieldsByKey = {
          first_name: { input: this.firstNameInput, message: this.dataset.firstNameError },
          last_name: { input: this.lastNameInput, message: this.dataset.lastNameError },
        };

        keys.forEach((key) => {
          const { input, message } = fieldsByKey[key];
          const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
          const errorMessageElement = this.querySelector(`#${errorMessageId}`);
          const errorTextElement = errorMessageElement?.querySelector('.error-message');

          if (errorTextElement) {
            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');
          }

          if (this.errorMessageList) {
            this.errorMessageList.appendChild(this.createErrorListItem(`#${input.id}`, message));
          }

          input.setAttribute('aria-invalid', true);
          input.setAttribute('aria-describedby', errorMessageId);
        });

        fieldsByKey[keys[0]].input.focus();
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        this.errorMessageWrapper.hidden = false;
        if (typeof body === 'object') {
          this.errorMessage.innerText = this.defaultErrorHeader;
          return Object.entries(body).forEach(([key, value]) => {           
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
            const message = `${value.join(', ')}`;
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');
            if (!errorTextElement) return;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', true);
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
        }

        this.errorMessage.innerText = body;
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        [this.emailInput, this.firstNameInput, this.lastNameInput, this.messageInput, this.sendonInput].forEach(
          (inputElement) => {
            inputElement.setAttribute('aria-invalid', false);
            inputElement.removeAttribute('aria-describedby');
          }
        );
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
        }
      }
    }
  );
}
